// Settings.js - Enhanced settings component for AI SDR with advanced team management
import React, { useState, useEffect, useContext } from "react";
import {
  FaCog,
  FaUsers,
  FaCreditCard,
  FaEnvelope,
  FaBell,
  FaStar,
  FaRegStar,
  FaSignOutAlt,
  FaPlus,
  FaEdit,
  FaTrash,
  FaUserPlus,
  FaUserMinus,
  FaSave,
  FaTimes,
  FaCheck,
  FaExclamationTriangle,
  FaShieldAlt,
  FaInfoCircle,
  FaSync,
} from "react-icons/fa";
import { AuthContext } from "../AuthProvider";
import { db } from "../firebase";
import {
  doc,
  getDoc,
  updateDoc,
  setDoc,
  collection,
  getDocs,
  query,
  where,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { toast } from "react-toastify";
import { clearAiSdrAccess } from "./components/AccessCodePopup";
import { useNavigate, useLocation } from "react-router-dom";
import useTeamAccess from "./hooks/useTeamAccess";

/**
 * Enhanced Settings Component with Advanced Team Management
 *
 * This component provides comprehensive team management capabilities similar to Echo Admin,
 * including team creation, owner management, role management, and access control.
 */
const Settings = () => {
  const [activeTab, setActiveTab] = useState("team");
  const { user } = useContext(AuthContext);
  const [defaultTeamId, setDefaultTeamId] = useState(null);
  const [isSettingDefault, setIsSettingDefault] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshTeams } = useTeamAccess();

  // Team management state
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [deletingTeam, setDeletingTeam] = useState(null);

  // Team form state
  const [teamForm, setTeamForm] = useState({
    name: "",
    description: "",
    owners: [],
    creators: [],
    readers: [],
  });

  // Member management state
  const [newOwnerEmail, setNewOwnerEmail] = useState("");
  const [newCreatorEmail, setNewCreatorEmail] = useState("");
  const [newReaderEmail, setNewReaderEmail] = useState("");
  const [ownerEmails, setOwnerEmails] = useState({});

  // Define settings tabs
  const tabs = [
    { id: "team", label: "Team Management", icon: <FaUsers /> },
    { id: "account", label: "Account", icon: <FaCog /> },
    { id: "billing", label: "Billing & Plans", icon: <FaCreditCard /> },
    { id: "notifications", label: "Notifications", icon: <FaBell /> },
    { id: "email", label: "Email Settings", icon: <FaEnvelope /> },
    { id: "access", label: "Access Control", icon: <FaSignOutAlt /> },
  ];

  useEffect(() => {
    if (user) {
      fetchUserProfile();
      loadUserTeams();
    }
  }, [user]);

  const fetchUserProfile = async () => {
    try {
      // Check both user document locations (from login.js pattern)
      const [userDocSnapshot, userNewDocSnapshot] = await Promise.all([
        getDoc(doc(db, "users", user.email)),
        getDoc(doc(db, "usersNew", user.uid)),
      ]);

      // Get default team from either document
      if (
        userNewDocSnapshot.exists() &&
        userNewDocSnapshot.data().defaultTeamId
      ) {
        setDefaultTeamId(userNewDocSnapshot.data().defaultTeamId);
      } else if (
        userDocSnapshot.exists() &&
        userDocSnapshot.data().defaultTeamId
      ) {
        setDefaultTeamId(userDocSnapshot.data().defaultTeamId);
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
    }
  };

  const loadUserTeams = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const teamsRef = collection(db, "teams");

      // Query teams where user is owner, creator, or reader
      const [ownerSnap, creatorSnap, readerSnap] = await Promise.all([
        getDocs(query(teamsRef, where("owners", "array-contains", user.uid))),
        getDocs(
          query(teamsRef, where("creators", "array-contains", user.email))
        ),
        getDocs(
          query(teamsRef, where("readers", "array-contains", user.email))
        ),
      ]);

      const teamsMap = new Map();

      // Process owner teams
      ownerSnap.forEach((doc) => {
        teamsMap.set(doc.id, {
          id: doc.id,
          ...doc.data(),
          role: "owner",
        });
      });

      // Process creator teams (don't override if user is already owner)
      creatorSnap.forEach((doc) => {
        if (!teamsMap.has(doc.id)) {
          teamsMap.set(doc.id, {
            id: doc.id,
            ...doc.data(),
            role: "creator",
          });
        }
      });

      // Process reader teams (don't override if user has higher role)
      readerSnap.forEach((doc) => {
        if (!teamsMap.has(doc.id)) {
          teamsMap.set(doc.id, {
            id: doc.id,
            ...doc.data(),
            role: "reader",
          });
        }
      });

      setTeams(Array.from(teamsMap.values()));
    } catch (error) {
      console.error("Error loading teams:", error);
      toast.error("Failed to load teams");
    } finally {
      setLoading(false);
    }
  };

  const setDefaultTeam = async (teamId, teamName) => {
    if (!user) return;

    setIsSettingDefault(true);
    try {
      // Update both user documents (following login.js pattern)
      await Promise.all([
        updateDoc(doc(db, "users", user.email), {
          defaultTeamId: teamId,
          updatedAt: new Date(),
        }),
        updateDoc(doc(db, "usersNew", user.uid), {
          defaultTeamId: teamId,
          updatedAt: new Date(),
        }),
      ]);

      setDefaultTeamId(teamId);
      toast.success(`Set "${teamName}" as your default team`);
    } catch (error) {
      console.error("Error updating default team:", error);
      toast.error("Failed to set default team");
    } finally {
      setIsSettingDefault(false);
    }
  };

  const handleSignOut = () => {
    clearAiSdrAccess();
    toast.success("You have been signed out of AI-SDR");
    navigate(`/ai-sdr${location.search}`);
  };

  // Team management functions
  const handleCreateTeam = async (e) => {
    e.preventDefault();

    if (!teamForm.name.trim()) {
      toast.error("Team name is required");
      return;
    }

    try {
      setLoading(true);
      const newTeamRef = doc(collection(db, "teams"));

      await setDoc(newTeamRef, {
        name: teamForm.name.trim(),
        description: teamForm.description.trim(),
        owners: [user.uid],
        creators: [],
        readers: [],
        createdAt: new Date(),
        updatedAt: new Date(),
      });

      toast.success("Team created successfully!");
      setShowCreateTeam(false);
      setTeamForm({
        name: "",
        description: "",
        owners: [],
        creators: [],
        readers: [],
      });
      loadUserTeams();

      // Refresh teams in the global context for immediate dropdown update
      if (refreshTeams) {
        setTimeout(refreshTeams, 500);
      }
    } catch (error) {
      console.error("Error creating team:", error);
      toast.error(error.message || "Failed to create team");
    } finally {
      setLoading(false);
    }
  };

  const handleEditTeam = async (e) => {
    e.preventDefault();

    if (!teamForm.name.trim()) {
      toast.error("Team name is required");
      return;
    }

    try {
      setLoading(true);
      await updateDoc(doc(db, "teams", editingTeam.id), {
        name: teamForm.name.trim(),
        description: teamForm.description.trim(),
        owners: teamForm.owners,
        creators: teamForm.creators,
        readers: teamForm.readers,
        updatedAt: new Date(),
      });

      toast.success("Team updated successfully!");
      setEditingTeam(null);
      setTeamForm({
        name: "",
        description: "",
        owners: [],
        creators: [],
        readers: [],
      });
      loadUserTeams();

      // Refresh teams in the global context for immediate dropdown update
      if (refreshTeams) {
        setTimeout(refreshTeams, 500);
      }
    } catch (error) {
      console.error("Error updating team:", error);
      toast.error(error.message || "Failed to update team");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTeam = async (teamId) => {
    try {
      setLoading(true);
      // TODO: Implement cascade deletion of team data
      await updateDoc(doc(db, "teams", teamId), {
        deleted: true,
        deletedAt: new Date(),
        deletedBy: user.uid,
      });

      toast.success("Team deleted successfully!");
      setDeletingTeam(null);
      loadUserTeams();

      // Refresh teams in the global context for immediate dropdown update
      if (refreshTeams) {
        setTimeout(refreshTeams, 500);
      }
    } catch (error) {
      console.error("Error deleting team:", error);
      toast.error("Failed to delete team");
    } finally {
      setLoading(false);
    }
  };

  const startEditTeam = (team) => {
    setEditingTeam(team);
    setSelectedTeam(team);
    setTeamForm({
      name: team.name || "",
      description: team.description || "",
      owners: team.owners || [],
      creators: team.creators || [],
      readers: team.readers || [],
    });
    setOwnerEmails({});
  };

  const cancelEdit = () => {
    setEditingTeam(null);
    setShowCreateTeam(false);
    setSelectedTeam(null);
    setTeamForm({
      name: "",
      description: "",
      owners: [],
      creators: [],
      readers: [],
    });
    setNewOwnerEmail("");
    setNewCreatorEmail("");
    setNewReaderEmail("");
    setOwnerEmails({});
  };

  // Helper function to add owner via backend API
  const addOwnerViaAPI = async (email, teamId) => {
    try {
      const idToken = await user.getIdToken();
      const apiBaseUrl =
        process.env.NODE_ENV === "development"
          ? "http://127.0.0.1:5001/damia-gcp/us-central1/api"
          : "https://us-central1-damia-gcp.cloudfunctions.net/api";

      const response = await fetch(`${apiBaseUrl}/addTeamOwnerByEmail`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          email: email,
          teamId: teamId,
          collection: "teams", // Use "teams" collection for AI-SDR
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to add owner");
      }

      return result;
    } catch (error) {
      console.error("Error adding owner via API:", error);
      throw error;
    }
  };

  // Helper function to remove owner via backend API
  const removeOwnerViaAPI = async (uid, teamId) => {
    try {
      const idToken = await user.getIdToken();
      const apiBaseUrl =
        process.env.NODE_ENV === "development"
          ? "http://127.0.0.1:5001/damia-gcp/us-central1/api"
          : "https://us-central1-damia-gcp.cloudfunctions.net/api";

      const response = await fetch(`${apiBaseUrl}/removeTeamOwner`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          uid: uid,
          teamId: teamId,
          collection: "teams", // Use "teams" collection for AI-SDR
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to remove owner");
      }

      return result;
    } catch (error) {
      console.error("Error removing owner via API:", error);
      throw error;
    }
  };

  const addOwner = async () => {
    const emailLowerCase = newOwnerEmail.trim().toLowerCase();

    if (!emailLowerCase || !isValidEmail(emailLowerCase)) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (!selectedTeam?.id) {
      toast.error("No team selected for editing");
      return;
    }

    try {
      // Use the backend API to add owner with the "teams" collection
      const result = await addOwnerViaAPI(emailLowerCase, selectedTeam.id);

      // Update local state with the new owner UID
      const newUid = result.addedUser.uid;

      setTeamForm((prev) => ({
        ...prev,
        owners: [...prev.owners, newUid],
      }));

      // Store the email for display
      setOwnerEmails((prev) => ({
        ...prev,
        [newUid]: emailLowerCase,
      }));

      setNewOwnerEmail("");
      toast.success(
        `Owner added: ${result.addedUser.email} (${
          result.addedUser.displayName || "No display name"
        })`
      );

      // Refresh the teams list to show updated data
      loadUserTeams();

      // Refresh teams in the global context for immediate dropdown update
      if (refreshTeams) {
        setTimeout(refreshTeams, 500);
      }
    } catch (error) {
      console.error("Error adding owner:", error);
      toast.error(error.message || "Failed to add owner");
    }
  };

  const removeOwner = async (uid) => {
    // Owner protection checks
    if (uid === user?.uid) {
      toast.error(
        "🔒 You cannot remove yourself as an owner. This prevents accidental loss of admin access.",
        { duration: 6000, position: "top-center" }
      );
      return;
    }

    const currentOwners = teamForm.owners || [];
    if (currentOwners.length <= 1) {
      toast.error(
        "🛡️ Cannot remove the last owner. Every team must have at least one owner to maintain access control.",
        { duration: 6000, position: "top-center" }
      );
      return;
    }

    if (!selectedTeam?.id) {
      toast.error("No team selected for editing");
      return;
    }

    try {
      // Use the backend API to remove owner
      await removeOwnerViaAPI(uid, selectedTeam.id);

      // Update local state
      setTeamForm((prev) => ({
        ...prev,
        owners: prev.owners.filter((u) => u !== uid),
      }));

      setOwnerEmails((prev) => {
        const newEmails = { ...prev };
        delete newEmails[uid];
        return newEmails;
      });

      const removedEmail = ownerEmails[uid] || `UID: ${uid}`;
      toast.success(`Owner removed: ${removedEmail}`, { duration: 4000 });

      // Refresh the teams list to show updated data
      loadUserTeams();

      // Refresh teams in the global context for immediate dropdown update
      if (refreshTeams) {
        setTimeout(refreshTeams, 500);
      }
    } catch (error) {
      console.error("Error removing owner:", error);
      toast.error(error.message || "Failed to remove owner");
    }
  };

  const addCreator = () => {
    const emailLowerCase = newCreatorEmail.trim().toLowerCase();
    if (emailLowerCase && !teamForm.creators.includes(emailLowerCase)) {
      setTeamForm((prev) => ({
        ...prev,
        creators: [...prev.creators, emailLowerCase],
      }));
      setNewCreatorEmail("");
    }
  };

  const removeCreator = (email) => {
    setTeamForm((prev) => ({
      ...prev,
      creators: prev.creators.filter((e) => e !== email),
    }));
  };

  const addReader = () => {
    const emailLowerCase = newReaderEmail.trim().toLowerCase();
    if (emailLowerCase && !teamForm.readers.includes(emailLowerCase)) {
      setTeamForm((prev) => ({
        ...prev,
        readers: [...prev.readers, emailLowerCase],
      }));
      setNewReaderEmail("");
    }
  };

  const removeReader = (email) => {
    setTeamForm((prev) => ({
      ...prev,
      readers: prev.readers.filter((e) => e !== email),
    }));
  };

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const canRemoveOwner = (uid) => {
    const currentOwners = teamForm.owners || [];

    if (uid === user?.uid) {
      return {
        canRemove: false,
        reason: "self",
        message: "Cannot remove yourself (prevents admin lockout)",
      };
    }

    if (currentOwners.length <= 1) {
      return {
        canRemove: false,
        reason: "lastOwner",
        message:
          "Cannot remove the last owner (teams must have at least one owner)",
      };
    }

    return {
      canRemove: true,
      reason: "safe",
      message: "Safe to remove",
    };
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <div className="flex-1 p-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Settings</h1>
        </div>

        <div className="bg-white shadow rounded-lg">
          {/* Tabs Navigation */}
          <div className="border-b">
            <nav className="flex">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  className={`px-6 py-4 text-sm font-medium flex items-center ${
                    activeTab === tab.id
                      ? "border-b-2 border-purple-600 text-purple-600"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <span className="mr-2">{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === "team" && (
              <div>
                {/* Team Management Header */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      Team Management
                    </h2>
                    <p className="text-gray-600">
                      Manage AI-SDR teams and permissions
                    </p>
                  </div>
                  <button
                    onClick={() => setShowCreateTeam(true)}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                  >
                    <FaPlus /> Create Team
                  </button>
                </div>

                {/* Teams List */}
                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-6">
                    {teams.map((team) => (
                      <div
                        key={team.id}
                        className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-lg font-semibold text-gray-900">
                                {team.name}
                              </h3>
                              {defaultTeamId === team.id && (
                                <span className="text-yellow-500 text-sm flex items-center">
                                  <FaStar className="mr-1" /> Default
                                </span>
                              )}
                            </div>
                            {team.description && (
                              <p className="text-gray-600 mt-1">
                                {team.description}
                              </p>
                            )}
                            <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                              <span>ID: {team.id}</span>
                              <span>Role: {team.role}</span>
                              <span>
                                Created:{" "}
                                {team.createdAt
                                  ? new Date(
                                      team.createdAt.seconds * 1000
                                    ).toLocaleDateString()
                                  : "N/A"}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setDefaultTeam(team.id, team.name)}
                              disabled={
                                isSettingDefault || defaultTeamId === team.id
                              }
                              className={`text-lg ${
                                defaultTeamId === team.id
                                  ? "text-yellow-500"
                                  : "text-gray-400 hover:text-yellow-500"
                              } transition-colors`}
                              title={
                                defaultTeamId === team.id
                                  ? "Default Team"
                                  : "Set as Default Team"
                              }
                            >
                              {defaultTeamId === team.id ? (
                                <FaStar />
                              ) : (
                                <FaRegStar />
                              )}
                            </button>
                            {team.role === "owner" && (
                              <>
                                <button
                                  onClick={() => startEditTeam(team)}
                                  className="text-blue-600 hover:text-blue-800 p-2 rounded-lg hover:bg-blue-50"
                                  title="Edit Team"
                                >
                                  <FaEdit />
                                </button>
                                <button
                                  onClick={() => setDeletingTeam(team)}
                                  className="text-red-600 hover:text-red-800 p-2 rounded-lg hover:bg-red-50"
                                  title="Delete Team"
                                >
                                  <FaTrash />
                                </button>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Team Members Summary */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="font-medium text-gray-900">
                                Owners
                              </h4>
                              <span className="text-xs text-gray-500">
                                ({team.owners?.length || 0})
                              </span>
                            </div>
                            <div className="space-y-1">
                              {team.owners && team.owners.length > 0 ? (
                                team.owners.slice(0, 3).map((uid, index) => (
                                  <div
                                    key={index}
                                    className={`px-2 py-1 rounded text-xs ${
                                      uid === user?.uid
                                        ? "text-purple-700 bg-purple-50 border border-purple-200"
                                        : "text-gray-600 bg-gray-50"
                                    }`}
                                  >
                                    UID: {uid}
                                    {uid === user?.uid && (
                                      <span className="text-purple-600 ml-1">
                                        • You
                                      </span>
                                    )}
                                  </div>
                                ))
                              ) : (
                                <span className="text-red-500 font-medium">
                                  No owners
                                </span>
                              )}
                              {team.owners && team.owners.length > 3 && (
                                <div className="text-xs text-gray-500">
                                  +{team.owners.length - 3} more
                                </div>
                              )}
                            </div>
                          </div>

                          <div>
                            <h4 className="font-medium text-gray-900 mb-2">
                              Creators
                            </h4>
                            <div className="space-y-1">
                              {team.creators && team.creators.length > 0 ? (
                                team.creators
                                  .slice(0, 2)
                                  .map((email, index) => (
                                    <div
                                      key={index}
                                      className="text-gray-600 bg-green-50 px-2 py-1 rounded text-xs"
                                    >
                                      {email}
                                    </div>
                                  ))
                              ) : (
                                <span className="text-gray-400 text-xs">
                                  No creators
                                </span>
                              )}
                              {team.creators && team.creators.length > 2 && (
                                <div className="text-xs text-gray-500">
                                  +{team.creators.length - 2} more
                                </div>
                              )}
                            </div>
                          </div>

                          <div>
                            <h4 className="font-medium text-gray-900 mb-2">
                              Readers
                            </h4>
                            <div className="space-y-1">
                              {team.readers && team.readers.length > 0 ? (
                                team.readers.slice(0, 2).map((email, index) => (
                                  <div
                                    key={index}
                                    className="text-gray-600 bg-blue-50 px-2 py-1 rounded text-xs"
                                  >
                                    {email}
                                  </div>
                                ))
                              ) : (
                                <span className="text-gray-400 text-xs">
                                  No readers
                                </span>
                              )}
                              {team.readers && team.readers.length > 2 && (
                                <div className="text-xs text-gray-500">
                                  +{team.readers.length - 2} more
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    {teams.length === 0 && (
                      <div className="text-center py-12">
                        <FaUsers className="mx-auto text-6xl text-gray-300 mb-4" />
                        <h3 className="text-lg font-medium text-gray-600 mb-2">
                          No teams yet
                        </h3>
                        <p className="text-gray-500 mb-6">
                          Create your first team to get started
                        </p>
                        <button
                          onClick={() => setShowCreateTeam(true)}
                          className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg transition-colors"
                        >
                          Create First Team
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeTab === "account" && (
              <div className="text-center py-8 text-gray-500">
                <FaCog className="mx-auto text-4xl mb-4 text-gray-400" />
                <h3 className="text-lg font-medium">Account Settings</h3>
                <p className="mt-2">Account settings will be available soon.</p>
              </div>
            )}

            {activeTab === "billing" && (
              <div className="text-center py-8 text-gray-500">
                <FaCreditCard className="mx-auto text-4xl mb-4 text-gray-400" />
                <h3 className="text-lg font-medium">Billing & Plans</h3>
                <p className="mt-2">
                  Billing and plan options will be available soon.
                </p>
              </div>
            )}

            {activeTab === "notifications" && (
              <div className="text-center py-8 text-gray-500">
                <FaBell className="mx-auto text-4xl mb-4 text-gray-400" />
                <h3 className="text-lg font-medium">Notification Settings</h3>
                <p className="mt-2">
                  Notification preferences will be available soon.
                </p>
              </div>
            )}

            {activeTab === "email" && (
              <div className="text-center py-8 text-gray-500">
                <FaEnvelope className="mx-auto text-4xl mb-4 text-gray-400" />
                <h3 className="text-lg font-medium">Email Settings</h3>
                <p className="mt-2">
                  Email configuration will be available soon.
                </p>
              </div>
            )}

            {activeTab === "access" && (
              <div className="py-8">
                <div className="max-w-md mx-auto">
                  <h3 className="text-lg font-medium mb-4">Access Control</h3>
                  <div className="mb-6">
                    <p className="text-gray-600 mb-4">
                      You're currently signed in to the AI-SDR module. You can
                      sign out to require the access code again.
                    </p>
                    <button
                      onClick={handleSignOut}
                      className="flex items-center px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                    >
                      <FaSignOutAlt className="mr-2" /> Sign Out of AI-SDR
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create/Edit Team Modal */}
      {(showCreateTeam || editingTeam) && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-start pt-10 z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  {editingTeam ? "Edit Team" : "Create New Team"}
                </h2>
                <button
                  onClick={cancelEdit}
                  className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100"
                >
                  <FaTimes />
                </button>
              </div>

              <form onSubmit={editingTeam ? handleEditTeam : handleCreateTeam}>
                {/* Basic Information */}
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Team Name *
                    </label>
                    <input
                      type="text"
                      value={teamForm.name}
                      onChange={(e) =>
                        setTeamForm((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Enter team name"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={teamForm.description}
                      onChange={(e) =>
                        setTeamForm((prev) => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Enter team description"
                      rows="3"
                    />
                  </div>
                </div>

                {/* Only show member management for editing existing teams */}
                {editingTeam && (
                  <>
                    {/* Owners Section */}
                    <div className="mb-6">
                      <div className="flex items-center justify-between mb-3">
                        <label className="block text-sm font-medium text-gray-700">
                          Owners (Full control over team and settings)
                        </label>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">
                            {teamForm.owners.length} owner
                            {teamForm.owners.length !== 1 ? "s" : ""}
                          </span>
                          {teamForm.owners.length === 1 && (
                            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full font-medium">
                              ⚠️ CRITICAL - Last Owner
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex gap-2 mb-3">
                        <input
                          type="email"
                          value={newOwnerEmail}
                          onChange={(e) => setNewOwnerEmail(e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          placeholder="Enter email address"
                        />
                        <button
                          type="button"
                          onClick={addOwner}
                          disabled={!isValidEmail(newOwnerEmail)}
                          className="bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg transition-colors"
                          title="Add owner by email"
                        >
                          <FaUserPlus />
                        </button>
                      </div>

                      <div className="space-y-2">
                        {teamForm.owners.map((uid, index) => {
                          const removeCheck = canRemoveOwner(uid);
                          return (
                            <div
                              key={index}
                              className={`flex items-center justify-between px-3 py-2 rounded-lg border ${
                                uid === user?.uid
                                  ? "bg-purple-50 border-purple-200"
                                  : "bg-red-50 border-red-200"
                              }`}
                            >
                              <div className="flex flex-col">
                                <div className="flex items-center gap-2">
                                  <span
                                    className={`font-medium ${
                                      uid === user?.uid
                                        ? "text-purple-800"
                                        : "text-red-800"
                                    }`}
                                  >
                                    UID: {uid}
                                  </span>
                                  {uid === user?.uid && (
                                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">
                                      YOU
                                    </span>
                                  )}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeOwner(uid)}
                                className={`p-1 rounded transition-colors ${
                                  !removeCheck.canRemove
                                    ? "text-gray-400 cursor-not-allowed bg-gray-100"
                                    : "text-red-600 hover:text-red-800 hover:bg-red-50"
                                }`}
                                disabled={!removeCheck.canRemove}
                                title={removeCheck.message}
                              >
                                <FaUserMinus />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Creators Section */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Creators (Can create and edit content)
                      </label>

                      <div className="flex gap-2 mb-3">
                        <input
                          type="email"
                          value={newCreatorEmail}
                          onChange={(e) => setNewCreatorEmail(e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          placeholder="Enter email address"
                        />
                        <button
                          type="button"
                          onClick={addCreator}
                          disabled={
                            !isValidEmail(newCreatorEmail) ||
                            teamForm.creators.includes(
                              newCreatorEmail.trim().toLowerCase()
                            )
                          }
                          className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg transition-colors"
                        >
                          <FaUserPlus />
                        </button>
                      </div>

                      <div className="space-y-2">
                        {teamForm.creators.map((email, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between bg-green-50 px-3 py-2 rounded-lg"
                          >
                            <span className="text-green-800">{email}</span>
                            <button
                              type="button"
                              onClick={() => removeCreator(email)}
                              className="text-red-600 hover:text-red-800 p-1 rounded"
                            >
                              <FaUserMinus />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Readers Section */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Readers (Can only view content)
                      </label>

                      <div className="flex gap-2 mb-3">
                        <input
                          type="email"
                          value={newReaderEmail}
                          onChange={(e) => setNewReaderEmail(e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          placeholder="Enter email address"
                        />
                        <button
                          type="button"
                          onClick={addReader}
                          disabled={
                            !isValidEmail(newReaderEmail) ||
                            teamForm.readers.includes(
                              newReaderEmail.trim().toLowerCase()
                            )
                          }
                          className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg transition-colors"
                        >
                          <FaUserPlus />
                        </button>
                      </div>

                      <div className="space-y-2">
                        {teamForm.readers.map((email, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between bg-blue-50 px-3 py-2 rounded-lg"
                          >
                            <span className="text-blue-800">{email}</span>
                            <button
                              type="button"
                              onClick={() => removeReader(email)}
                              className="text-red-600 hover:text-red-800 p-1 rounded"
                            >
                              <FaUserMinus />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {/* Form Actions */}
                <div className="flex items-center justify-end space-x-4 pt-4 border-t">
                  <button
                    type="button"
                    onClick={cancelEdit}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-colors"
                  >
                    <FaSave /> {editingTeam ? "Update Team" : "Create Team"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingTeam && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <FaExclamationTriangle className="text-red-600 text-2xl mr-3" />
                <h2 className="text-xl font-bold text-gray-900">Delete Team</h2>
              </div>

              <p className="text-gray-600 mb-6">
                Are you sure you want to delete "{deletingTeam.name}"? This will
                permanently delete the team and may affect associated data.
              </p>

              <p className="text-red-600 font-medium mb-6">
                This action cannot be undone.
              </p>

              <div className="flex items-center justify-end space-x-4">
                <button
                  onClick={() => setDeletingTeam(null)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteTeam(deletingTeam.id)}
                  disabled={loading}
                  className="bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-colors"
                >
                  <FaTrash /> Delete Team
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
